import React from "react";
import { MAX_PROJECT_CLIPS, PROJECT_STORAGE_KEY } from "./videoProjectConstants";

/** @typedef {Object} ProjectClip
 * @property {string} id
 * @property {string} url
 * @property {number} duration
 * @property {string} modelId
 * @property {string} modelLabel
 * @property {string} [aspectRatio]
 * @property {string} [outputResolution]
 * @property {number} [jobId]
 * @property {string} [thumbnailUrl]
 * @property {number} [segmentIndex] 1-based scene index for auto-stitch runs
 * @property {number} [segmentTotal]
 * @property {'ready'|'failed'|'generating'|'pending'} [status]
 */

function loadStored() {
  try {
    const raw = localStorage.getItem(PROJECT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(clips) {
  try {
    localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(clips));
  } catch {
    // ignore quota errors
  }
}

function newClipId() {
  return `clip-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** True when a board clip can be stitched / dragged (URL present, not a placeholder). */
export function isClipReady(clip) {
  if (!clip || !String(clip.url || "").trim()) return false;
  const s = clip.status;
  return s !== "pending" && s !== "generating" && s !== "failed";
}

const VideoProjectContext = React.createContext(null);

export function VideoProjectProvider({ children }) {
  const [clips, setClips] = React.useState(() => loadStored());

  React.useEffect(() => {
    persist(clips);
  }, [clips]);

  const normalizeClip = React.useCallback((clip, id = newClipId()) => {
    const url = String(clip?.url || "").trim();
    const status =
      clip.status ||
      (url ? "ready" : clip.segmentIndex != null ? "pending" : "failed");
    return {
      id,
      url,
      duration: Math.max(1, Math.round(Number(clip.duration) || 5)),
      modelId: String(clip.modelId || ""),
      modelLabel: String(clip.modelLabel || clip.modelId || "Video"),
      aspectRatio: clip.aspectRatio || null,
      outputResolution: clip.outputResolution || null,
      jobId: clip.jobId ?? null,
      thumbnailUrl: clip.thumbnailUrl || url || null,
      segmentIndex: clip.segmentIndex ?? null,
      segmentTotal: clip.segmentTotal ?? null,
      status,
      errorMessage: clip.errorMessage || null,
    };
  }, []);

  const addClip = React.useCallback((clip) => {
    const url = String(clip?.url || "").trim();
    const isPlaceholder =
      clip?.segmentIndex != null &&
      (clip?.status === "failed" ||
        clip?.status === "pending" ||
        clip?.status === "generating");
    if (!url && !isPlaceholder) {
      return { ok: false, reason: "missing_url", added: 0 };
    }

    let added = 0;
    setClips((prev) => {
      if (prev.length >= MAX_PROJECT_CLIPS) return prev;
      if (url && prev.some((c) => c.url === url)) return prev;
      if (
        clip.segmentIndex != null &&
        prev.some((c) => c.segmentIndex === clip.segmentIndex)
      ) {
        return prev.map((c) =>
          c.segmentIndex === clip.segmentIndex ? normalizeClip(clip, c.id) : c,
        );
      }
      added = 1;
      return [...prev, normalizeClip(clip)];
    });
    return added ? { ok: true, added: 1 } : { ok: false, reason: "duplicate_or_full", added: 0 };
  }, [normalizeClip]);

  /** Replace or insert a clip keyed by 1-based segmentIndex (auto-stitch scenes). */
  const upsertSegmentClip = React.useCallback((clip) => {
    const segIdx = Number(clip?.segmentIndex);
    if (!Number.isFinite(segIdx) || segIdx < 1) {
      return addClip(clip);
    }
    setClips((prev) => {
      const existing = prev.find((c) => c.segmentIndex === segIdx);
      const nextClip = normalizeClip(clip, existing?.id || newClipId());
      const without = prev.filter((c) => c.segmentIndex !== segIdx);
      return [...without, nextClip].sort(
        (a, b) => (a.segmentIndex || 0) - (b.segmentIndex || 0),
      );
    });
    return { ok: true };
  }, [addClip, normalizeClip]);

  /**
   * Keep N scene placeholders in sync with the form plan.
   * Preserves ready/generating/failed clips when the slot count/duration aligns.
   */
  const syncScenePlaceholders = React.useCallback(
    (scenes, meta = {}, opts = {}) => {
      const list = Array.isArray(scenes) ? scenes : [];
      if (!list.length) {
        setClips((prev) => prev.filter((c) => c.segmentIndex == null));
        return;
      }
      const total = list.length;
      const modelId = String(meta.modelId || "");
      const modelLabel = String(meta.modelLabel || modelId || "Video");
      const aspectRatio = meta.aspectRatio || null;
      const outputResolution = meta.outputResolution || null;
      const forcePending = Boolean(opts.forcePending);

      setClips((prev) => {
        const extras = prev.filter((c) => c.segmentIndex == null);
        const bySeg = new Map(
          prev
            .filter((c) => Number(c.segmentIndex) >= 1)
            .map((c) => [Number(c.segmentIndex), c]),
        );
        const next = list.map((scene, i) => {
          const segIdx = i + 1;
          const duration = Math.max(1, Math.round(Number(scene.duration) || 5));
          const existing = bySeg.get(segIdx);
          if (existing && !forcePending) {
            const keepStatus =
              existing.status === "ready" ||
              existing.status === "generating" ||
              existing.status === "failed"
                ? existing.status
                : "pending";
            return normalizeClip(
              {
                ...existing,
                duration,
                segmentIndex: segIdx,
                segmentTotal: total,
                modelId: existing.modelId || modelId,
                modelLabel: existing.modelLabel || modelLabel,
                aspectRatio: existing.aspectRatio || aspectRatio,
                outputResolution: existing.outputResolution || outputResolution,
                status: keepStatus,
                url: keepStatus === "ready" || keepStatus === "generating" ? existing.url || "" : "",
                thumbnailUrl:
                  keepStatus === "ready" ? existing.thumbnailUrl || existing.url : null,
                jobId:
                  keepStatus === "ready" || keepStatus === "generating" ? existing.jobId : null,
                errorMessage: keepStatus === "failed" ? existing.errorMessage : null,
              },
              existing.id,
            );
          }
          return normalizeClip({
            url: "",
            duration,
            modelId,
            modelLabel,
            aspectRatio,
            outputResolution,
            segmentIndex: segIdx,
            segmentTotal: total,
            status: "pending",
          });
        });
        return [...next, ...extras].slice(0, MAX_PROJECT_CLIPS);
      });
    },
    [normalizeClip],
  );

  /** Drop scene N and every later scene (continuity chain must restart from N). */
  const removeSegmentsFrom = React.useCallback((fromSegmentIndex) => {
    const from = Number(fromSegmentIndex);
    if (!Number.isFinite(from) || from < 1) return;
    setClips((prev) =>
      prev.filter((c) => c.segmentIndex == null || c.segmentIndex < from),
    );
  }, []);

  /** Reset scene N+ to pending placeholders (without removing earlier ready clips). */
  const resetSegmentsFrom = React.useCallback((fromSegmentIndex, scenes, meta = {}) => {
    const from = Number(fromSegmentIndex);
    if (!Number.isFinite(from) || from < 1) return;
    const list = Array.isArray(scenes) ? scenes : [];
    const total = list.length;
    setClips((prev) => {
      const kept = prev.filter(
        (c) => c.segmentIndex == null || Number(c.segmentIndex) < from,
      );
      const placeholders = list
        .map((scene, i) => {
          const segIdx = i + 1;
          if (segIdx < from) return null;
          return normalizeClip({
            url: "",
            duration: Math.max(1, Math.round(Number(scene.duration) || 5)),
            modelId: meta.modelId || "",
            modelLabel: meta.modelLabel || meta.modelId || "Video",
            aspectRatio: meta.aspectRatio || null,
            outputResolution: meta.outputResolution || null,
            segmentIndex: segIdx,
            segmentTotal: total,
            status: "pending",
          });
        })
        .filter(Boolean);
      return [...kept, ...placeholders]
        .sort((a, b) => (a.segmentIndex || 999) - (b.segmentIndex || 999))
        .slice(0, MAX_PROJECT_CLIPS);
    });
  }, [normalizeClip]);

  /** @param {import("./VideoProjectContext").ProjectClip[]} incoming */
  const addClips = React.useCallback((incoming) => {
    const list = Array.isArray(incoming) ? incoming : [];
    let added = 0;
    setClips((prev) => {
      const next = [...prev];
      const urls = new Set(prev.map((c) => c.url).filter(Boolean));
      for (const clip of list) {
        if (next.length >= MAX_PROJECT_CLIPS) break;
        const url = String(clip?.url || "").trim();
        if (!url || urls.has(url)) continue;
        urls.add(url);
        added += 1;
        next.push({
          id: newClipId(),
          url,
          duration: Math.max(1, Math.round(Number(clip.duration) || 5)),
          modelId: String(clip.modelId || ""),
          modelLabel: String(clip.modelLabel || clip.modelId || "Video"),
          aspectRatio: clip.aspectRatio || null,
          outputResolution: clip.outputResolution || null,
          jobId: clip.jobId ?? null,
          thumbnailUrl: clip.thumbnailUrl || url,
          status: "ready",
        });
      }
      return next;
    });
    return { added };
  }, []);

  const removeClip = React.useCallback((id) => {
    setClips((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const reorderClips = React.useCallback((fromIndex, toIndex) => {
    setClips((prev) => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const clearProject = React.useCallback(() => {
    setClips([]);
  }, []);

  const replaceClips = React.useCallback((incoming) => {
    const list = Array.isArray(incoming) ? incoming : [];
    const next = [];
    const urls = new Set();
    for (const clip of list) {
      if (next.length >= MAX_PROJECT_CLIPS) break;
      const url = String(clip?.url || "").trim();
      const isPlaceholder =
        clip?.segmentIndex != null &&
        (clip.status === "pending" || clip.status === "generating" || clip.status === "failed");
      if (!url && !isPlaceholder) continue;
      if (url) {
        if (urls.has(url)) continue;
        urls.add(url);
      }
      next.push({
        id: newClipId(),
        url,
        duration: Math.max(1, Math.round(Number(clip.duration) || 5)),
        modelId: String(clip.modelId || ""),
        modelLabel: String(clip.modelLabel || clip.modelId || "Video"),
        aspectRatio: clip.aspectRatio || null,
        outputResolution: clip.outputResolution || null,
        jobId: clip.jobId ?? null,
        thumbnailUrl: clip.thumbnailUrl || url || null,
        segmentIndex: clip.segmentIndex ?? null,
        segmentTotal: clip.segmentTotal ?? null,
        status: clip.status || (url ? "ready" : "pending"),
        errorMessage: clip.errorMessage || null,
      });
    }
    setClips(next);
    return { count: next.length };
  }, []);

  const isInProject = React.useCallback(
    (url) => clips.some((c) => c.url === url),
    [clips],
  );

  const totalDuration = React.useMemo(
    () =>
      clips.reduce((sum, c) => {
        if (c.status === "pending" || c.status === "failed") return sum + (Number(c.duration) || 0);
        return sum + (Number(c.duration) || 0);
      }, 0),
    [clips],
  );

  const readyClips = React.useMemo(
    () => clips.filter((c) => isClipReady(c)),
    [clips],
  );

  const value = React.useMemo(
    () => ({
      clips,
      readyClips,
      totalDuration,
      addClip,
      addClips,
      upsertSegmentClip,
      syncScenePlaceholders,
      removeSegmentsFrom,
      resetSegmentsFrom,
      removeClip,
      reorderClips,
      replaceClips,
      clearProject,
      isInProject,
      maxClips: MAX_PROJECT_CLIPS,
    }),
    [
      clips,
      readyClips,
      totalDuration,
      addClip,
      addClips,
      upsertSegmentClip,
      syncScenePlaceholders,
      removeSegmentsFrom,
      resetSegmentsFrom,
      removeClip,
      reorderClips,
      replaceClips,
      clearProject,
      isInProject,
    ],
  );

  return (
    <VideoProjectContext.Provider value={value}>{children}</VideoProjectContext.Provider>
  );
}

export function useVideoProject() {
  const ctx = React.useContext(VideoProjectContext);
  if (!ctx) {
    throw new Error("useVideoProject must be used within VideoProjectProvider");
  }
  return ctx;
}
